import { auth } from "../lib/firebase";

interface WebActivityConfig {
  apiUrl: string;
  tenantId: string;
  orgId?: string;
}

interface GuestUserData {
  contactId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

interface WebActivityEvent {
  event_type: string;
  tenant_id: string;
  org_id?: string;
  session_id: string;
  client_id?: string;
  user_id?: string;
  user_email?: string;
  guest?: GuestUserData;
  page_url: string;
  page_title?: string;
  referrer?: string;
  event_category?: string;
  event_action?: string;
  event_label?: string;
  event_value?: number;
  properties?: Record<string, any>;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  screen_resolution?: string;
  viewport_size?: string;
  country?: string;
  region?: string;
  city?: string;
  _retry_id?: string; // Internal tracking ID for retries
  _retry_count?: number; // Internal tracking for retry count
}

class WebActivityService {
  private config: WebActivityConfig | null = null;
  private sessionId: string;
  private clientId: string;
  private guestData: GuestUserData | null = null;
  private queue: WebActivityEvent[] = [];
  private isOnline: boolean = navigator.onLine;
  private flushTimer: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private readonly MAX_RETRIES = 3; // Maximum number of retries for failed events
  private retryMap: Map<string, number> = new Map(); // Track retry attempts per event

  constructor() {
    this.sessionId = this.generateSessionId();
    this.clientId = this.getOrCreateClientId();

    // Listen for online/offline events
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    // Flush events before page unload
    window.addEventListener("beforeunload", this.flushEventsSync);
  }

  initialize(config: WebActivityConfig) {
    this.config = config;
    this.startFlushTimer();
  }

  setGuestData(guestData: GuestUserData | null) {
    this.guestData = guestData;
    console.log("Guest data set for web activity tracking:", guestData);
  }

  getGuestData(): GuestUserData | null {
    return this.guestData;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getOrCreateClientId(): string {
    const stored = localStorage.getItem("web_activity_client_id");
    if (stored) return stored;

    const clientId = `client_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    localStorage.setItem("web_activity_client_id", clientId);
    return clientId;
  }

  private startFlushTimer() {
    if (this.flushTimer) clearInterval(this.flushTimer);

    this.flushTimer = setInterval(() => {
      if (this.queue.length > 0) {
        this.flushEvents();
      }
    }, this.FLUSH_INTERVAL);
  }

  private handleOnline = () => {
    this.isOnline = true;
    this.flushEvents();
  };

  private handleOffline = () => {
    this.isOnline = false;
  };

  private getViewportSize(): string {
    return `${window.innerWidth}x${window.innerHeight}`;
  }

  private getScreenResolution(): string {
    return `${screen.width}x${screen.height}`;
  }

  async trackEvent(
    eventData: Partial<WebActivityEvent> & { event_type: string }
  ) {
    if (!this.config) {
      console.warn("WebActivityService not initialized");
      return;
    }

    // Get current user if authenticated
    const currentUser = auth.currentUser;

    // Generate a stable retry ID for this event
    const retryId = `${eventData.event_type}_${
      this.sessionId
    }_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

    const event: WebActivityEvent = {
      ...eventData,
      tenant_id: this.config.tenantId,
      org_id: this.config.orgId,
      session_id: this.sessionId,
      client_id: this.clientId,
      user_id: currentUser?.uid || eventData.user_id,
      user_email: currentUser?.email || eventData.user_email,
      // Include guest data if user is not authenticated and guest data is available
      guest: !currentUser && this.guestData ? this.guestData : undefined,
      page_url: eventData.page_url || window.location.href,
      page_title: eventData.page_title || document.title,
      referrer: eventData.referrer || document.referrer,
      viewport_size: this.getViewportSize(),
      screen_resolution: this.getScreenResolution(),
      // UTM parameters
      utm_source:
        eventData.utm_source ||
        new URLSearchParams(window.location.search).get("utm_source") ||
        undefined,
      utm_medium:
        eventData.utm_medium ||
        new URLSearchParams(window.location.search).get("utm_medium") ||
        undefined,
      utm_campaign:
        eventData.utm_campaign ||
        new URLSearchParams(window.location.search).get("utm_campaign") ||
        undefined,
      utm_term:
        eventData.utm_term ||
        new URLSearchParams(window.location.search).get("utm_term") ||
        undefined,
      utm_content:
        eventData.utm_content ||
        new URLSearchParams(window.location.search).get("utm_content") ||
        undefined,
      // Add retry tracking
      _retry_id: retryId,
      _retry_count: 0,
    };

    // Add to queue
    this.queue.push(event);

    // Flush high-intent conversion events immediately. Seller valuation flows
    // often navigate or open an auth overlay right after submission, so waiting
    // for the 5s timer can lose the only event that proves intent.
    if (
      event.event_type === "valuation_request" ||
      event.event_type === "lead_capture" ||
      this.queue.length >= this.BATCH_SIZE
    ) {
      await this.flushEvents();
    }
  }

  private async flushEvents() {
    if (!this.config || !this.isOnline || this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    try {
      // Get auth token if user is authenticated
      let authToken: string | undefined;
      if (auth.currentUser) {
        try {
          authToken = await auth.currentUser.getIdToken();
        } catch (error) {
          console.warn("Failed to get auth token:", error);
        }
      }

      // Send events to backend
      const promises = events.map((event) => {
        // Use the event's retry ID and count
        const retryId = event._retry_id || `fallback_${Date.now()}`;
        const retryCount = event._retry_count || 0;

        // Skip events that have exceeded max retries
        if (retryCount >= this.MAX_RETRIES) {
          console.warn(
            `Event ${retryId} exceeded maximum retry attempts (${this.MAX_RETRIES}), dropping event:`,
            event
          );
          return Promise.resolve();
        }

        return fetch(this.config!.apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
          body: JSON.stringify(event),
        })
          .then(async (response) => {
            if (!response.ok) {
              const errorText = await response.text();
              console.error(
                "Event tracking failed:",
                response.status,
                errorText
              );

              // Increment retry count and re-queue only if under max retries
              if (retryCount < this.MAX_RETRIES) {
                // Increment the retry count on the event itself
                event._retry_count = retryCount + 1;
                this.queue.push(event);
                console.log(
                  `Requeuing event ${retryId}, attempt ${retryCount + 1}/${
                    this.MAX_RETRIES
                  }`
                );
              }
            }
          })
          .catch((error) => {
            console.error("Failed to send event:", error);

            // Increment retry count and re-queue only if under max retries
            if (retryCount < this.MAX_RETRIES) {
              // Increment the retry count on the event itself
              event._retry_count = retryCount + 1;
              this.queue.push(event);
              console.log(
                `Requeuing event ${retryId} after error, attempt ${
                  retryCount + 1
                }/${this.MAX_RETRIES}`
              );
            }
          });
      });

      await Promise.all(promises);
    } catch (error) {
      console.error("Failed to flush events:", error);

      // Re-queue all events but respect retry limits
      events.forEach((event) => {
        const retryCount = event._retry_count || 0;

        if (retryCount < this.MAX_RETRIES) {
          // Increment the retry count on the event itself
          event._retry_count = retryCount + 1;
          this.queue.push(event);
        }
      });
    }
  }

  private flushEventsSync = () => {
    if (!this.config || this.queue.length === 0) return;

    const events = [...this.queue];

    // Use sendBeacon for reliable delivery on page unload. The Cloud Function
    // accepts both a single event and {events: [...]}; keep the root endpoint so
    // browser unload delivery does not 404 on /batch.
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify({ events })], {
        type: "application/json",
      });
      navigator.sendBeacon(this.config.apiUrl, blob);
    } else {
      // Fallback to synchronous XHR
      const xhr = new XMLHttpRequest();
      xhr.open("POST", this.config.apiUrl, false);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(JSON.stringify({ events }));
    }
  };

  // Convenience methods for common events
  trackPageView(properties?: Record<string, any>) {
    this.trackEvent({
      event_type: "page_view",
      event_category: "Navigation",
      event_action: "Page View",
      properties,
    });
  }

  trackClick(selector: string, properties?: Record<string, any>) {
    this.trackEvent({
      event_type: "click",
      event_category: "Interaction",
      event_action: "Click",
      event_label: selector,
      properties,
    });
  }

  trackSearch(
    query: string,
    resultCount: number,
    properties?: Record<string, any>
  ) {
    this.trackEvent({
      event_type: "search",
      event_category: "Search",
      event_action: "Search Query",
      event_label: query,
      event_value: resultCount,
      properties: {
        ...properties,
        search_query: query,
        result_count: resultCount,
      },
    });
  }

  trackPropertyView(propertyId: string, properties?: Record<string, any>) {
    this.trackEvent({
      event_type: "property_view",
      event_category: "Property",
      event_action: "View Property",
      event_label: propertyId,
      properties: {
        ...properties,
        property_id: propertyId,
      },
    });
  }

  trackLeadCapture(source: string, properties?: Record<string, any>) {
    this.trackEvent({
      event_type: "lead_capture",
      event_category: "Conversion",
      event_action: "Lead Capture",
      event_label: source,
      properties,
    });
  }

  // Clean up
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    window.removeEventListener("beforeunload", this.flushEventsSync);
    this.flushEvents();
  }
}

export const webActivityService = new WebActivityService();
export type { GuestUserData };
